/**
 * ПУБЛІЧНА сторінка підписання документа контрагентом (Фаза 1 ЕДО, 2026-08-13). Відкривається за
 * посиланням /edo/sign/<token> БЕЗ логіна: документ + підпис КЕП у браузері — ключ НЕ покидає
 * пристрій контрагента. Підпис (CAdES, p7s) зберігається вкладенням до документа відправника.
 *
 * ч.1442 (рішення власника): сторінка в дизайні Облік+; три способи підпису — Дія.Підпис
 * (віджет id.gov.ua), хмарний ключ / токен (віджет ІІТ), файловий ключ; замість «завантажити собі»
 * — зберегти документ з підписами у свій журнал ЕДО (посилання живе 60 днів).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import WidgetSign from '@/shared/eusign/WidgetSign';
import BrandLogo from '@/shared/components/BrandLogo';
import EdoClaimPanel from '@/shared/components/EdoClaimPanel';
import { LegalDocLink } from '@/shared/components/LegalConsent';
import { isLoggedIn } from '@/auth/store';
import {
  detailOf, edoSign, hasSession, PENDING_CLAIM_KEY, type PublicSignInfo,
} from '@/api/edoSign';

type Method = 'diia' | 'cloud' | 'file';

const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

function rememberPending(token: string): void {
  try { localStorage.setItem(PENDING_CLAIM_KEY, token); } catch { /* приватне вікно */ }
}

/** Типовий спосіб: Дія, якщо підключено робоче середовище id.gov.ua; у тестовому — файловий ключ,
 * щоб контрагент зі звичайним ключем не впирався у вікно, яке приймає лише тестові ключі. */
export function defaultMethod(info: Pick<PublicSignInfo, 'widgets'>): Method {
  const diia = info.widgets?.diia;
  return diia && !diia.test_env ? 'diia' : 'file';
}

export default function EdoSignPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [method, setMethod] = useState<Method | null>(null);

  const infoQ = useQuery({
    queryKey: ['edo-sign-info', token],
    queryFn: () => edoSign.info(token),
    enabled: !!token,
    retry: false,
  });
  const info = infoQ.data;
  const current: Method = method ?? (info ? defaultMethod(info) : 'file');

  useEffect(() => {
    let url: string | null = null;
    if (info) {
      edoSign.fileBlob(token).then((b) => { url = URL.createObjectURL(b); setFileUrl(url); });
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [info, token]);

  const signFile = async () => {
    setBusy(true);
    setErr(null);
    try {
      const eu = await import('@/shared/eusign/eusign');
      await eu.loadEuSign();
      const fileInput = document.getElementById('edo-key-file') as HTMLInputElement;
      const pwdInput = document.getElementById('edo-key-pwd') as HTMLInputElement;
      const keyFile = fileInput?.files?.[0];
      if (!keyFile) { setErr('Оберіть файл ключа (*.dat, *.pfx, *.jks, *.zs2)'); return; }
      if (!pwdInput?.value) { setErr('Введіть пароль ключа'); return; }
      const keyBytes = new Uint8Array(await keyFile.arrayBuffer());
      // null CA → авто-перебір АЦСК по CMP (юзеру не треба обирати видавця).
      const owner = await eu.readPrivateKey(keyBytes, pwdInput.value, null, keyFile.name);
      // CAdES-T з міткою часу; TSP недоступний → BES-фолбек (сервер це помітить).
      const { signature } = await eu.signDataInternalCadesT(await edoSign.file(token));
      await edoSign.submit(token, {
        signature_b64: eu.bytesToBase64(signature),
        signer_name: owner?.subjCN ?? '',
        signer_edrpou: owner?.subjEDRPOUCode ?? (owner as { subjDRFOCode?: string })?.subjDRFOCode ?? '',
      });
      setDone(true);
    } catch (e) {
      setErr(detailOf(e, (e as { message?: string })?.message ?? 'Не вдалося підписати'));
    } finally {
      setBusy(false);
    }
  };

  const submitWidget = async (sigB64: string) => {
    try {
      await edoSign.submit(token, { signature_b64: sigB64 });
      setDone(true);
    } catch (e) {
      throw new Error(detailOf(e, 'Не вдалося надіслати підпис'));
    }
  };

  const returnTo = `/edo/sign/${token}`;
  const header = useMemo(() => (
    <header className="esign__top">
      <a href="/" className="esign__brand" aria-label="Облік+ — на головну"><BrandLogo variant="header" /></a>
      <span className="esign__tag">Електронний документообіг</span>
      <span className="esign__spacer" />
      {hasSession()
        ? <Link className="esign__link" to="/edo/journal">мій журнал ЕДО</Link>
        : <>
          <a className="esign__link" href={`/login?return_to=${encodeURIComponent(returnTo)}`}
            onClick={() => rememberPending(token)}>увійти</a>
          <a className="esign__link" href="/signup" onClick={() => rememberPending(token)}>зареєструватись</a>
        </>}
    </header>
  ), [returnTo, token]);

  if (infoQ.isError) {
    return (
      <div className="esign">
        {header}
        <div className="card esign__center">
          <h1>Посилання недійсне</h1>
          <p className="esign__note">
            Посилання на підписання не знайдено або його строк минув (посилання діє 60 днів).
            Зверніться до відправника документа.
          </p>
        </div>
      </div>
    );
  }
  if (!info) {
    return <div className="esign">{header}<p className="esign__center esign__note">Завантаження…</p></div>;
  }

  const signed = done || info.status === 'SIGNED';
  const diia = info.widgets?.diia ?? null;
  const expires = fmtDate(info.expires_at);

  return (
    <div className="esign">
      {header}
      <main className="esign__grid">
        <div className="esign__col">
          <section className="card esign__info" aria-label="Документ">
            <div className="esign__eyebrow">Документ на підписання</div>
            <h1>{info.subject ?? info.filename}</h1>
            <dl className="esign__facts">
              <dt>Від кого</dt><dd><b>{info.org_name}</b></dd>
              {info.subject && info.subject !== info.filename && <><dt>Файл</dt><dd>{info.filename}</dd></>}
              <dt>Підпис відправника</dt>
              <dd>{info.sender_signed
                ? <span className="esign__ok">✓ підписано{info.sender_signer ? ` — ${info.sender_signer}` : ''}</span>
                : 'немає'}</dd>
              {info.expected_signer_code && <><dt>Підписує</dt><dd>ключ з кодом <b>{info.expected_signer_code}</b></dd></>}
              <dt>Посилання діє до</dt><dd>{expires}</dd>
            </dl>
          </section>
          <section className="card esign__doc" aria-label="Перегляд документа">
            {!fileUrl && <p className="esign__note">Завантаження документа…</p>}
            {fileUrl && (info.mime_type === 'application/pdf'
              ? <iframe src={fileUrl} title="документ" className="esign__preview" />
              : <img src={fileUrl} alt="документ" className="esign__preview-img" />)}
          </section>
        </div>

        <div className="esign__col">
          <section className="card esign__sign" aria-label="Підпис">
            {signed ? (
              <div className="esign__done" role="status" data-testid="edo-signed">
                <b>Документ підписано.</b> Відправник уже бачить ваш підпис.
              </div>
            ) : (
              <>
                <h2>Підписати документ</h2>
                {info.expected_signer_code && (
                  <p className="esign__note">
                    Підпис приймається лише ключем з кодом <b>{info.expected_signer_code}</b> (ЄДРПОУ / РНОКПП).
                  </p>
                )}
                <div className="seg-tabs" role="tablist" aria-label="Спосіб підпису">
                  {diia && (
                    <button type="button" role="tab" aria-selected={current === 'diia'}
                      className={current === 'diia' ? 'active' : ''} onClick={() => setMethod('diia')}>
                      Дія.Підпис
                    </button>
                  )}
                  <button type="button" role="tab" aria-selected={current === 'cloud'}
                    className={current === 'cloud' ? 'active' : ''} onClick={() => setMethod('cloud')}>
                    хмарний ключ / токен
                  </button>
                  <button type="button" role="tab" aria-selected={current === 'file'}
                    className={current === 'file' ? 'active' : ''} onClick={() => setMethod('file')}>
                    файловий ключ
                  </button>
                </div>
                {current === 'diia' && diia && (
                  <>
                    {diia.test_env && (
                      <div className="esign__warn" data-testid="diia-test-env">
                        Дію підключено в тестовому середовищі: тут підписують лише тестовими ключами. Звичайним
                        ключем підпишіть на вкладці «файловий ключ» або «хмарний ключ / токен».
                      </div>
                    )}
                    <WidgetSign widgetUri={diia.sign_url} loaderSrc={diia.loader}
                      providerLabel="Дія.Підпис, файл, токен або хмарний ключ"
                      getData={() => edoSign.file(token)} onSigned={submitWidget} />
                  </>
                )}
                {current === 'cloud' && (
                  <WidgetSign providerLabel="хмарний ключ, токен або файл"
                    getData={() => edoSign.file(token)} onSigned={submitWidget} />
                )}
                {current === 'file' && (
                  <div className="esign__file">
                    <p className="esign__note" style={{ margin: 0 }}>
                      Ключ і пароль обробляються лише у вашому браузері й нікуди не передаються.
                    </p>
                    <div className="field">
                      <label htmlFor="edo-key-file">Файл ключа</label>
                      <input id="edo-key-file" type="file" accept=".dat,.pfx,.pk8,.zs2,.jks" />
                    </div>
                    <div className="field">
                      <label htmlFor="edo-key-pwd">Пароль ключа</label>
                      <input id="edo-key-pwd" type="password" autoComplete="off" />
                    </div>
                    <div className="esign__actions">
                      <button type="button" className="primary" onClick={() => void signFile()} disabled={busy}>
                        {busy ? 'підписання…' : 'підписати'}
                      </button>
                    </div>
                    {err && <p className="esign__error" role="alert">{err}</p>}
                  </div>
                )}
              </>
            )}
          </section>

          <section className="card esign__save" aria-label="Зберегти в Облік+" data-testid="edo-save">
            <h2>{signed ? 'Збережіть підписаний документ в Облік+' : 'Зберігайте документи в Облік+'}</h2>
            {hasSession() ? (
              <EdoClaimPanel token={token} />
            ) : (
              <>
                <ul>
                  <li>Посилання діє до <b>{expires}</b> — потім документ за ним не відкриється.</li>
                  <li>В Облік+ документ лежить у журналі ЕДО разом із підписами відправника і вашим —
                    не загубиться в пошті чи «Завантаженнях».</li>
                  <li>Архів з документом і підписами завантажите з журналу будь-коли.</li>
                  <li>Наступні документи підписуйте прямо в журналі: Дія.Підпис, хмарний або файловий ключ.</li>
                  <li>Режим «ЕДО» — безкоштовно до 20 документів.</li>
                </ul>
                <div className="esign__actions">
                  <a className="primary" href="/signup" onClick={() => rememberPending(token)}>зареєструватись</a>
                  {isLoggedIn()
                    ? <Link className="secondary" to="/select-tenant" state={{ next: returnTo }}>обрати акаунт</Link>
                    : <a className="secondary" href={`/login?return_to=${encodeURIComponent(returnTo)}`}
                      onClick={() => rememberPending(token)}>увійти</a>}
                </div>
                <p className="esign__note" style={{ marginBottom: 0 }}>
                  Після реєстрації додайте свою організацію чи ФОП
                  {info.expected_signer_code ? ` з кодом ${info.expected_signer_code}` : ' з кодом, яким підписали документ'},
                  підтвердіть її ключем КЕП — і збережіть документ у свій журнал.
                </p>
              </>
            )}
          </section>
        </div>
      </main>
      <footer className="esign__foot">
        Облік+ — онлайн-бухгалтерія і електронний документообіг ·{' '}
        <LegalDocLink doc="terms">оферта</LegalDocLink> · <LegalDocLink doc="privacy">конфіденційність</LegalDocLink>
      </footer>
    </div>
  );
}
